import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { alertService } from '@unpispas/upp-base';
import { eventbusService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { clockService } from '@unpispas/upp-base';
import { loginService } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { UserView } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';

import { EVTBUS_EDIT_USER } from '@unpispas/upp-base';

@Component({
    selector: 'upp-topbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-topbar.html',
    styleUrls: [
      './upp-topbar.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]    
})
export class UppTopBarComponent extends languageSupport implements OnInit, AfterViewInit, OnDestroy {
    private _clock_subscription: Subscription | null = null;
    private _load_subscription: Subscription | null = null;
    private _online_subscription: Subscription | null = null;
    private _view_subscription: Subscription | null = null;
    private _tab_subscription: Subscription | null = null;
    private _user_subscription: Subscription | null = null;
    private _place_subscription: Subscription | null = null;
    private _change_subscription: Subscription | null = null;

    constructor(private lang: languageService, private change: ViewRenderer, private clock: clockService, private data: dataService, private login: loginService, private alertCtrl: alertService, private evtbus: eventbusService, public view: viewService){
        super(lang, change.cdref);
    }

    private _clock: Date | null = null;
    ngOnInit(): void {
        this._clock_subscription = this.clock.OnOneSecondTick.subscribe(
        () => {
            const _date = new Date();
            if (!this._clock || _date.getMinutes() != this._clock.getMinutes()){
                this._clock = _date;
                this.change.markForCheck();
            }
        });

        this._load_subscription = this.data.OnRefreshPercent.subscribe(
        (percent) => {
            this.LoadPercent = percent;
        })

        this._online_subscription = this.view.OnOnline.subscribe(
        () => {
            this.change.markForCheck();
        });

        this._view_subscription = this.view.OnViewChanged.subscribe(
        () => {
            this.change.markForCheck();
        });

        this._tab_subscription = this.view.OnTabChanged.subscribe(
        () => {
            this.change.markForCheck();
        });

        if (this.user) {
            this._user_subscription = this.user.OnRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }

        this._BindPlaceRefresh();

        this._change_subscription = this.data.OnPlaceChanged.subscribe(
        () => {
            this._BindPlaceRefresh();
            this.change.markForCheck();
        });
    }

    /**
     * Subscribes to the active place refresh stream; rebinds when the session place changes.
     */
    private _BindPlaceRefresh(): void {
        if (this._place_subscription != null){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        const _place = this.place;
        if (_place){
            this._place_subscription = _place.OnRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }
    }

    ngAfterViewInit(): void {
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        if (this._clock_subscription != null){
            this._clock_subscription.unsubscribe();
            this._clock_subscription = null;
        }

        if (this._load_subscription != null){
            this._load_subscription.unsubscribe();
            this._load_subscription = null;
        }

        if (this._online_subscription != null){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }

        if (this._view_subscription != null){
            this._view_subscription.unsubscribe();
            this._view_subscription = null;
        }

        if (this._tab_subscription != null){
            this._tab_subscription.unsubscribe();
            this._tab_subscription = null;
        }

        if (this._user_subscription != null){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        if (this._place_subscription != null){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._change_subscription != null){
            this._change_subscription.unsubscribe();
            this._change_subscription = null;
        }
    }

    get user(): UserView | null {
        return this.data.alives.get(this.data.user) as UserView;
    }

    get place(): PlaceView | null {
        return this.data.alives.get(this.data.place) as PlaceView;
    }

    get Clock(){
        return {
            Hours: this._clock?.getHours().toString().padStart(2, '0') || '00',
            Minutes: this._clock?.getMinutes().toString().padStart(2, '0') || '00'
        };
    }

    get WaiterTables(){
        const _tables = this.place?.AlertTables ?? [];
        const _attending = this.view.ScndTab === 'table'
            ? (this.view.PanelArg?.table as QrCodeView | null | undefined)?.object ?? null
            : null;
        if (!_attending){
            return _tables;
        }

        return _tables.filter((_table) => _table !== _attending);
    }

    get TopMessage(){
        if (!this.view.IsOnline){
            return 'OFFLINE';
        }

        if (this.user?.IsPending){
            return 'USERPENDING';
        }

        if (this.place && this.place.IsLoaded && !this.place.ToInsert){
            if (this.place.IsExpired){
                return 'EXPIRED';
            }

            if (this.place.IsExpiring){
                return 'EXPIRING';
            }

            if (!this.place.WSStorageReady){
                return 'NOSERVICE';
            }

            if (this.place.FiscalWarning){
                return 'FISCAL';
            }

            if (this.place.PosWarning){
                return 'NOTPV';
            }

            if (this.WaiterTables.length > 0){
                return 'WAITER';
            }
        }

        if (this.view.SwUpdate){
            return 'SWUPDATE';
        }

        return null;
    }

    private _loadpercent = 0;
    get LoadPercent(){
        return this._loadpercent;
    }

    set LoadPercent(value){
        if (this._loadpercent != value){
            this._loadpercent = value;
            this.change.markForCheck();        
        }
    }

    get IsLoading(){
        return (this.LoadPercent < 1);
    }

    EditUser(){
        this.evtbus.emit<UserView | null>(EVTBUS_EDIT_USER, this.user);
    }

    private async DoLogout(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@menu_logout_confirm_title'),
            message: this.lang.tr('@menu_logout_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        (document.activeElement as HTMLElement)?.blur();
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        (document.activeElement as HTMLElement)?.blur();
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then((detail:any) => {
            if (detail && detail.data){
                this.login.DoLogout();
            }
        });

        await alert.present();
    }

    private DoExit(){
        this.data.SetPlace(null).then(
        () => {
            this.view.View = 'USER';
        });
    }

    OnClose(){
        if (this.place){
            this.DoExit();
        }
        else {
            this.DoLogout();
        }
    }

    /**
     * Opens the tables tab; with a single waiter alert, also opens that table panel.
     */
    async OnWaiter(): Promise<void> {
        if (!this.place || this.WaiterTables.length === 0){
            return;
        }

        const _allow = await this.view.GrantViewChange();
        if (!_allow){
            console.info("[TOPBAR] Waiter warning navigation cancelled by user");
            return;
        }

        this.view.Mode = 'VIEW';
        this.view.SetMainTab('VIEW', 'placetables');

        if (this.WaiterTables.length === 1){
            const _table = this.data.alives.get(this.WaiterTables[0]) as QrCodeView | null;
            if (_table){
                this.view.ScndTab = 'table';
                this.view.PanelArg = {
                    table: _table
                };
            }
            else {
                this.view.ScndTab = null;
                this.view.PanelArg = null;
            }
        }
        else {
            this.view.ScndTab = null;
            this.view.PanelArg = null;
        }

        this.change.markForCheck();
    }

    async OnFiscal(){
        const _allow = await this.view.GrantViewChange();
        if (!_allow){
            console.info("[TOPBAR] Fiscal warning navigation cancelled by user");
            return;
        }

        this.view.Mode = 'EDIT';
        this.view.SetMainTab('EDIT', 'fiscalinfo');
    }

    async OnConfigurePos(){
        const _allow = await this.view.GrantViewChange();
        if (!_allow){
            console.info("[TOPBAR] POS warning navigation cancelled by user");
            return;
        }

        this.view.Mode = 'EDIT';
        this.view.SetMainTab('EDIT', 'deviceinfo');
    }

    /**
     * Opens place edit on the license renewal secondary panel (`ScndTab = 'license'` → `licenseinfo`).
     */
    async OnExpiredLicense(){
        const _allow = await this.view.GrantViewChange();
        if (!_allow){
            console.info("[TOPBAR] License warning navigation cancelled by user");
            return;
        }

        this.view.Mode = 'EDIT';
        this.view.SetMainTab('EDIT', 'placeinfo');
        this.view.ScndTab = 'license';
    }

    OnUpdate(){
        console.log("TODO: pasa por aqui: OnUpdate");   // TODO
    }
}